/**
 * Lightweight analytics wrapper for dotConverse.
 *
 * Fans out every `track()` call to:
 *  - window.dataLayer  (read by GTM container GTM-PLNTCPGC)
 *  - window.gtag       (gtag.js loader for GA4 ID G-XBXGSNGE6M)
 *  - window.posthog    (kept for legacy parity if/when PostHog is loaded)
 *
 * Both GTM and gtag.js are injected from /public/index.html, so this module
 * does not need to lazy-load any SDK. If the page is rendered without the
 * snippet (e.g. tests), the helper silently no-ops.
 *
 * Event names follow GA4 / snake_case convention. Form submissions also fire
 * the GA4-recommended `generate_lead` event via `trackLead()` so it lights up
 * as a conversion candidate in GA4 admin.
 */

const isBrowser = () => typeof window !== "undefined";

function pushDataLayer(event, params) {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
}

function pushGtag(event, params) {
  if (!isBrowser() || typeof window.gtag !== "function") return;
  window.gtag("event", event, params);
}

function pushPosthog(event, params) {
  if (!isBrowser()) return;
  try {
    if (window.posthog?.capture) window.posthog.capture(event, params);
  } catch {
    /* swallow */
  }
}

export function track(event, props = {}) {
  if (!event) return;
  const enriched = {
    ...(isBrowser()
      ? {
          page_path: window.location.pathname,
          page_location: window.location.href,
        }
      : {}),
    ...props,
  };
  pushDataLayer(event, enriched);
  pushGtag(event, enriched);
  pushPosthog(event, enriched);
}

// GA4-recommended `generate_lead` event for any successful form submission.
// Used in addition to the granular `*_submit_success` events so marketing
// can map this directly to a conversion in GA4 admin.
export function trackLead(leadType, extra = {}) {
  track("generate_lead", {
    method: leadType,
    lead_type: leadType,
    ...extra,
  });
}

// SPA pageviews. The gtag.js bootstrap fires a page_view on initial load via
// the `config` call; we fire one more on every client-side route change.
export function trackPageView(path, title) {
  const params = {
    page_path: path,
    page_location: isBrowser() ? window.location.href : undefined,
    page_title: title || (isBrowser() ? document.title : undefined),
  };
  pushDataLayer("page_view", params);
  pushGtag("page_view", params);
}

let sliderTimeouts = {};
export function trackSliderDebounced(name, value, extra = {}) {
  if (sliderTimeouts[name]) clearTimeout(sliderTimeouts[name]);
  sliderTimeouts[name] = setTimeout(() => {
    track("calculator_slider_change", { slider: name, value, ...extra });
  }, 650);
}
